"use client";

import backgroundOverlay from "@/app/(public)/shared/assets/background-overlay.png";
import alphaExplorationLogo from "@/app/(public)/shared/assets/logos/alphaexplorationco.png";
import amexLogo from "@/app/(public)/shared/assets/logos/amex.png";
import googleLogo from "@/app/(public)/shared/assets/logos/google.svg";
import notionLogo from "@/app/(public)/shared/assets/logos/notion.png";
import nutrienLogo from "@/app/(public)/shared/assets/logos/nutrien.svg";
import offerfitLogo from "@/app/(public)/shared/assets/logos/offerfit.svg";
import onAndOffsiteLogo from "@/app/(public)/shared/assets/logos/onandoffsite.svg";
import prodLogo from "@/app/(public)/shared/assets/logos/prod.svg";
import stripeLogo from "@/app/(public)/shared/assets/logos/stripe.png";
import supabaseLogo from "@/app/(public)/shared/assets/logos/supabase.png";
import windsurfLogo from "@/app/(public)/shared/assets/logos/windsurf.png";
import ycombinatorLogo from "@/app/(public)/shared/assets/logos/ycombinator.png";
import { motion } from "framer-motion";
import React, { useEffect, useRef, useState } from "react";

// --- Testimonial data type ---
export interface Testimonial {
  id: string;
  type: string; // e.g. "startup", "enterprise", "general"
  companyName: string;
  companyLogoUrl: string;
  personFirstName: string;
  personLastName: string;
  personRole: string;
  testimonialContent: string;
  venueName?: string;
  venueLocation?: string;
  logo: string;
  logoWidth?: number;
  logoHeight?: number;
  author: string;
  title?: string;
  text: string;
}

export interface Logo {
  name: string;
  logo: string;
  width?: number;
  height?: number;
}

// --- Production Testimonials ---
export const productionTestimonials: Testimonial[] = [
  {
    id: "yeon",
    type: "startup",
    companyName: "Google",
    companyLogoUrl: googleLogo.src,
    personFirstName: "Yeon",
    personLastName: "Baik",
    personRole: "Senior Manager",
    testimonialContent:
      "Using Nowadays, I secured bids from hotel venues with beautiful facilities at prices I'd never been able to negotiate before. Nowadays found us the perfect venue that fit all of our criteria.",
    venueName: "The Island Resort",
    venueLocation: "Laguna Beach, CA",
    logo: googleLogo.src,
    logoWidth: 120,
    logoHeight: 40,
    author: "Yeon Baik",
    title: "Senior Manager @ Google",
    text: "Using Nowadays, I secured bids from hotel venues with beautiful facilities at prices I'd never been able to negotiate before. Nowadays found us the perfect venue that fit all of our criteria.",
  },
  {
    id: "sharleen",
    type: "startup",
    companyName: "Offerfit",
    companyLogoUrl: offerfitLogo.src,
    personFirstName: "Sharleen",
    personLastName: "Lazartes",
    personRole: "Office Manager",
    testimonialContent:
      "I can't believe the venue sourcing was done so quickly. I can spend all of that saved time on figuring out the programming and bonding activities. That's my favorite part of planning.",
    venueName: "Hotel Indigo",
    venueLocation: "Austin, TX",
    logo: offerfitLogo.src,
    logoWidth: 130,
    logoHeight: 40,
    author: "Sharleen Lazartes",
    title: "Office Manager @ Offerfit",
    text: "I can't believe the venue sourcing was done so quickly. I can spend all of that saved time on figuring out the programming and bonding activities. That's my favorite part of planning.",
  },
  {
    id: "janet",
    type: "startup",
    companyName: "AEC",
    companyLogoUrl: alphaExplorationLogo.src,
    personFirstName: "Janet",
    personLastName: "Kim",
    personRole: "Executive Assistant",
    testimonialContent:
      "Oh my god. I really like this. I don't have to reach out to all 100 places myself. This is great, super helpful!",
    venueName: "Alpha Exploration HQ",
    venueLocation: "San Francisco, CA",
    logo: alphaExplorationLogo.src,
    logoWidth: 240,
    logoHeight: 40,
    author: "Janet Kim",
    title: "Executive Assistant @ AEC",
    text: "Oh my god. I really like this. I don't have to reach out to all 100 places myself. This is great, super helpful!",
  },
  {
    id: "audrey",
    type: "startup",
    companyName: "On and Offsites",
    companyLogoUrl: onAndOffsiteLogo.src,
    personFirstName: "Audrey",
    personLastName: "Kim",
    personRole: "Founder",
    testimonialContent:
      "OH MY GOSH the quotes for the offsite venues were so helpful and amazing — I can't believe Nowadays got this all done so quickly!",
    venueName: "On and Offsites HQ",
    venueLocation: "Remote",
    logo: onAndOffsiteLogo.src,
    logoWidth: 175,
    logoHeight: 40,
    author: "Audrey Kim",
    title: "Founder @ On and Offsites",
    text: "OH MY GOSH the quotes for the offsite venues were so helpful and amazing — I can't believe Nowadays got this all done so quickly!",
  },
  {
    id: "david-blansfield",
    type: "enterprise",
    companyName: "Northstar Travel Group",
    companyLogoUrl: "/northstar-meetings-logo.png",
    personFirstName: "David",
    personLastName: "Blansfield",
    personRole: "Executive Vice President",
    testimonialContent:
      "The recommendations we received from Nowadays for destinations and activities were incredibly helpful in planning our partner offsite for 2024.",
    venueName: "YC Partner Offsite",
    venueLocation: "California",
    logo: ycombinatorLogo.src,
    logoWidth: 170,
    logoHeight: 60,
    author: "Renee Mars",
    title: "Head of HR @ Y Combinator",
    text: "The recommendations we received from Nowadays for destinations and activities were incredibly helpful in planning our partner offsite for 2024.",
  },
  {
    id: "anne",
    type: "startup",
    companyName: "Prod",
    companyLogoUrl: prodLogo.src,
    personFirstName: "Anne",
    personLastName: "Brandes",
    personRole: "President",
    testimonialContent:
      "You're actually doing something really original, and you have a core innovation, which is automating the process of getting hotel quotes, and I think that is awesome.",
    venueName: "Prod HQ",
    venueLocation: "Remote",
    logo: prodLogo.src,
    logoWidth: 120,
    logoHeight: 40,
    author: "Anne Brandes",
    title: "President @ Prod",
    text: "You're actually doing something really original, and you have a core innovation, which is automating the process of getting hotel quotes, and I think that is awesome.",
  },
  {
    id: "inderpal",
    type: "startup",
    companyName: "Zelus",
    companyLogoUrl: "/landing/zelus.png",
    personFirstName: "Inderpal",
    personLastName: "Singh",
    personRole: "Chief Operating Officer",
    testimonialContent:
      "Using Nowadays has made a significant impact in planning for our fully remote company. Nowadays is at the level as our previous event planners for a fraction of the cost.",
    venueName: "Zelus Analytics",
    venueLocation: "Remote",
    logo: "/landing/zelus.png",
    logoWidth: 60,
    logoHeight: 60,
    author: "Inderpal Singh",
    title: "Chief Operating Officer @ Zelus",
    text: "Using Nowadays has made a significant impact in planning for our fully remote company. Nowadays is at the level as our previous event planners for a fraction of the cost.",
  },
  {
    id: "david",
    type: "enterprise",
    companyName: "Layer N",
    companyLogoUrl: "/landing/layern.png",
    personFirstName: "David",
    personLastName: "Cao",
    personRole: "Chief Operating Officer",
    testimonialContent:
      "I was very surprised by Nowadays' results and how good they were. I had also spent some time researching venues myself, as I find it enjoyable, and ended up finding the same top results that Nowadays recommended.",
    venueName: "Layer N HQ",
    venueLocation: "Remote",
    logo: "/landing/layern.png",
    logoWidth: 60,
    logoHeight: 40,
    author: "David Cao",
    title: "Chief Operating Officer @ Layer N",
    text: "I was very surprised by Nowadays' results and how good they were. I had also spent some time researching venues myself, as I find it enjoyable, and ended up finding the same top results that Nowadays recommended.",
  },
  {
    id: "laura",
    type: "enterprise",
    companyName: "Supabase",
    companyLogoUrl: supabaseLogo.src,
    personFirstName: "Laura",
    personLastName: "C.",
    personRole: "Head of People and Operations",
    testimonialContent:
      "We can confidently say that this has been our team's top rated and best catered event made possible by amazing staff and management. It was a week we will never forget and always cherish.",
    venueName: "Supabase HQ",
    venueLocation: "Remote",
    logo: supabaseLogo.src,
    logoWidth: 150,
    logoHeight: 60,
    author: "Laura C.",
    title: "Head of People and Operations @ Supabase",
    text: "We can confidently say that this has been our team's top rated and best catered event made possible by amazing staff and management. It was a week we will never forget and always cherish.",
  },
  {
    id: "renee",
    type: "enterprise",
    companyName: "Y Combinator",
    companyLogoUrl: ycombinatorLogo.src,
    personFirstName: "Renee",
    personLastName: "Mars",
    personRole: "Head of HR",
    testimonialContent:
      "The recommendations we received from Nowadays for destinations and activities were incredibly helpful in planning our partner offsite for 2024.",
    venueName: "YC Partner Offsite",
    venueLocation: "California",
    logo: ycombinatorLogo.src,
    logoWidth: 55,
    logoHeight: 55,
    author: "Renee Mars",
    title: "Head of HR @ Y Combinator",
    text: "The recommendations we received from Nowadays for destinations and activities were incredibly helpful in planning our partner offsite for 2024.",
  },
  {
    id: "gianna",
    type: "travel-agent",
    companyName: "Windsurf",
    companyLogoUrl: windsurfLogo.src,
    personFirstName: "Gianna",
    personLastName: "",
    personRole: "Travel Agent",
    testimonialContent:
      "We need a better event-management platform that really solves the pain points of these RFPs… this seems more robust and I feel like it's got more legs to do more of the kinds of things that could be super helpful.",
    venueName: "Windsurf",
    venueLocation: "Remote",
    logo: windsurfLogo.src,
    logoWidth: 120,
    logoHeight: 40,
    author: "Gianna",
    title: "Travel Agent @ Windsurf",
    text: "We need a better event-management platform that really solves the pain points of these RFPs… this seems more robust and I feel like it's got more legs to do more of the kinds of things that could be super helpful.",
  },
];

// --- Map from type to array of testimonial ids ---
export const testimonialTypeMap: Record<string, string[]> = {
  startup: ["yeon", "sharleen", "janet", "audrey", "inderpal"],
  enterprise: ["renee", "david", "laura", "audrey", "anne", "david-blansfield"],
  general: ["yeon", "sharleen", "laura", "david", "anne", "david-blansfield"],
  "travel-agent": ["gianna"],
};

// --- Map from id to testimonial object ---
export const testimonialIdMap: Record<string, Testimonial> = Object.fromEntries(
  productionTestimonials.map((t) => [t.id, t])
);

// --- Sample logos for logo-row-featured variant ---
export const sampleLogos = [
  { name: "Google", logo: googleLogo.src, width: 120, height: 40 },
  { name: "Notion", logo: notionLogo.src, width: 120, height: 40 },
  { name: "Nutrien", logo: nutrienLogo.src, width: 120, height: 40 },
  { name: "Stripe", logo: stripeLogo.src, width: 120, height: 40 },
  { name: "Amex", logo: amexLogo.src, width: 120, height: 40 },
];

// --- Component Props ---
interface TestimonialProps {
  variant: "wall" | "single" | "logo-row-featured";
  type?: string; // e.g. "startup", "enterprise", "general"
  testimonials?: Testimonial[]; // Optional: override testimonials
  logos?: Logo[]; // for logo-row-featured
  featuredIndex?: number; // for single/featured (deprecated, use person instead)
  person?: string; // for single - testimonial id (e.g. "janet", "yeon", "anne")
  className?: string;
  title?: string; // Optional: custom section title
}

const Testimonial: React.FC<TestimonialProps> = ({
  variant,
  type,
  testimonials,
  logos = [],
  featuredIndex = 0,
  person,
  className = "",
  title,
}) => {
  // --- Select testimonials by type if type is provided ---
  let testimonialsToShow: Testimonial[] = [];
  if (type && testimonialTypeMap[type]) {
    testimonialsToShow = testimonialTypeMap[type].map((id) => testimonialIdMap[id]).filter(Boolean);
  } else if (testimonials && testimonials.length > 0) {
    testimonialsToShow = testimonials;
  } else {
    testimonialsToShow = productionTestimonials;
  }

  // --- Wall Variant (Wall of Love style) ---
  if (variant === "wall") {
    // Limit to maximum 9 testimonials for 3x3 grid
    const limitedTestimonials = testimonialsToShow.slice(0, 9);

    // 3 columns, chunk testimonials
    const columns = [[], [], []] as Testimonial[][];
    limitedTestimonials.forEach((t, i) => columns[i % 3].push(t));
    return (
      <div className={`w-full flex flex-col items-center py-8 md:py-16 ${className}`}>
        {title && (
          <div className="text-left md:text-center px-4 md:px-8 mb-6 md:mb-8 w-full">
            <h3 className="text-h3 text-landing-primary">{title}</h3>
          </div>
        )}
        {/* Mobile: Single column grid */}
        <div className="block md:hidden w-full px-4">
          <div className="flex flex-col gap-4">
            {limitedTestimonials.slice(0, 6).map((testimonial) => (
              <div key={testimonial.id} className="bg-white rounded-xl p-4 border border-gray-200 flex flex-col gap-3">
                {testimonial.logo && (
                  <img
                    src={testimonial.logo}
                    alt="Company logo"
                    className="h-3 object-contain mb-1 self-start"
                    style={{
                      width: (testimonial.logoWidth || 80) * 0.6,
                      height: (testimonial.logoHeight || 32) * 0.6,
                    }}
                  />
                )}
                <div className="text-body text-landing-primary text-left">"{testimonial.text}"</div>
                <div className="flex flex-col gap-1 text-left">
                  <div className="text-body text-landing-primary font-medium">{testimonial.author}</div>
                  {testimonial.title && <div className="text-body text-landing-secondary">{testimonial.title}</div>}
                </div>
              </div>
            ))}
          </div>
        </div>
        {/* Desktop: Three column layout */}
        <div className="hidden md:flex gap-6 lg:gap-8 w-full max-w-6xl justify-center px-4">
          {columns.map((col, colIdx) => (
            <div key={colIdx} className="flex flex-col gap-6 lg:gap-8 flex-1">
              {col.map((testimonial) => (
                <div
                  key={testimonial.id}
                  className="bg-white rounded-xl p-6 border border-gray-200 flex flex-col gap-4"
                >
                  {testimonial.logo && (
                    <img
                      src={testimonial.logo}
                      alt="Company logo"
                      className="h-4 object-contain mb-2 self-start"
                      style={{
                        width: (testimonial.logoWidth || 80) * 0.7,
                        height: (testimonial.logoHeight || 32) * 0.7,
                      }}
                    />
                  )}
                  <div className="text-body text-landing-primary text-left">"{testimonial.text}"</div>
                  <div className="flex flex-col gap-1 text-left">
                    <div className="text-body text-landing-primary font-medium">{testimonial.author}</div>
                    {testimonial.title && <div className="text-body text-landing-secondary">{testimonial.title}</div>}
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    );
  }

  // --- Single Variant (Enterprise style) ---
  if (variant === "single") {
    let t: Testimonial | undefined;

    if (person) {
      // Use person prop to directly lookup testimonial by id
      t = testimonialIdMap[person];
    } else {
      // Fallback to featuredIndex for backwards compatibility
      t = testimonialsToShow[featuredIndex] || testimonialsToShow[0];
    }

    if (!t) return null;
    return (
      <div className={`w-full flex flex-col items-center py-8 md:py-16 ${className}`}>
        {title && (
          <div className="text-left md:text-center w-full mb-6 md:mb-8">
            <h3 className="text-h3 text-landing-primary">{title}</h3>
          </div>
        )}
        <div className="bg-landing-secondary rounded-2xl overflow-hidden flex flex-col md:flex-row min-h-[280px] md:min-h-[320px] w-full max-w-4xl">
          {/* Left: Quote */}
          <div className="flex-1 flex flex-col justify-center p-6 md:p-8 lg:p-12">
            <p className="text-tag-lg md:text-tag-xl text-landing-primary mb-4 md:mb-6 text-left">"{t.text}"</p>
            <div className="flex flex-col gap-1 text-left">
              <span className="text-body font-semibold text-landing-primary">{t.author}</span>
              {t.title && <span className="text-body text-landing-secondary">{t.title}</span>}
            </div>
          </div>
          {/* Right: Logo */}
          {t.logo && (
            <div className="relative flex-1 flex items-center justify-center bg-white p-6 md:p-8">
              <img
                src={t.logo}
                alt="Company logo"
                className="max-w-full h-auto object-contain"
                style={{
                  width: t.logoWidth ? Math.min(t.logoWidth * 1.2, 200) : 200,
                  height: t.logoHeight ? Math.min(t.logoHeight * 1.2, 80) : 80,
                  maxWidth: "100%",
                }}
              />
            </div>
          )}
        </div>
      </div>
    );
  }

  // --- Logo Row + Featured Variant (wall-style with floating cards) ---
  if (variant === "logo-row-featured") {
    // Show 5 logos on desktop, 4 on mobile
    const minLogos = 5;
    const mobileLogos = 4;
    let featuredTestimonials = testimonialsToShow;
    if (featuredTestimonials.length < minLogos) {
      featuredTestimonials = Array.from(
        { length: minLogos },
        (_, i) => testimonialsToShow[i % testimonialsToShow.length]
      );
    } else {
      featuredTestimonials = featuredTestimonials.slice(0, minLogos);
    }

    // State for active testimonial and auto-advance control
    const [activeIndex, setActiveIndex] = useState(0);
    const [progress, setProgress] = useState(0);
    const intervalRef = useRef<NodeJS.Timeout | null>(null);
    const progressRef = useRef<number>(0);
    const startTimeRef = useRef<number>(Date.now());

    // Auto-advance logic - use different counts for mobile vs desktop
    useEffect(() => {
      const advanceToNext = () => {
        // Check if we're on mobile (window width < 768px)
        const isMobile = typeof window !== "undefined" && window.innerWidth < 768;
        const maxIndex = isMobile ? mobileLogos : minLogos;
        setActiveIndex((prev) => (prev + 1) % maxIndex);
        startTimeRef.current = Date.now();
        setProgress(0);
        progressRef.current = 0;
      };

      intervalRef.current = setInterval(advanceToNext, 10000);

      return () => {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
        }
      };
    }, [minLogos, mobileLogos]);

    // Progress bar animation
    useEffect(() => {
      startTimeRef.current = Date.now();
      setProgress(0);
      progressRef.current = 0;

      // Update progress every 100ms for smooth animation
      const progressInterval = setInterval(() => {
        const elapsed = Date.now() - startTimeRef.current;
        const duration = 10000;
        const newProgress = Math.min(elapsed / duration, 1);
        setProgress(newProgress);
      }, 100);

      return () => clearInterval(progressInterval);
    }, [activeIndex]);

    // Handle logo click with proper timer reset
    const handleLogoClick = (idx: number) => {
      if (idx === activeIndex) return;

      // Clear existing interval
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }

      // Update active index and reset progress
      setActiveIndex(idx);
      startTimeRef.current = Date.now();
      setProgress(0);
      progressRef.current = 0;

      // Start new interval immediately
      const advanceToNext = () => {
        // Check if we're on mobile (window width < 768px)
        const isMobile = typeof window !== "undefined" && window.innerWidth < 768;
        const maxIndex = isMobile ? mobileLogos : minLogos;
        setActiveIndex((prev) => (prev + 1) % maxIndex);
        startTimeRef.current = Date.now();
        setProgress(0);
        progressRef.current = 0;
      };

      intervalRef.current = setInterval(advanceToNext, 10000);
    };

    // Use logos from testimonials
    const logoRow = featuredTestimonials.map((ft) => ({
      name: ft.companyName,
      logo: ft.companyLogoUrl,
      width: 80,
      height: 32,
    }));

    const t = featuredTestimonials[activeIndex];
    if (!t) return null;

    return (
      <div className={`w-full flex flex-col items-center  ${className}`}>
        {/* Main testimonial window with background - wall style */}
        <div className="w-full">
          <div className="flex flex-col gap-4 md:gap-6">
            {/* Large background container with single testimonial */}
            <div
              className="w-full max-w-4xl mx-auto h-[50vh] md:h-[400px] relative rounded-2xl md:rounded-3xl overflow-hidden"
              style={{
                boxShadow: "inset 0 0 0 1px var(--landing-border-primary)",
              }}
            >
              {/* Background overlay for texture */}
              <div
                className="absolute inset-0 rounded-2xl md:rounded-3xl opacity-10"
                style={{
                  backgroundImage: `url(${backgroundOverlay.src})`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                  backgroundRepeat: "no-repeat",
                  zIndex: 1,
                }}
              />

              {/* Single testimonial content */}
              <div className="absolute inset-0 flex items-center justify-center p-4 md:p-8 z-10">
                <div className="text-left md:text-center max-w-3xl w-full">
                  <motion.div
                    key={t.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.4 }}
                    className="w-full"
                  >
                    <blockquote className="text-h3 text-landing-primary mb-4 md:mb-6 leading-relaxed">
                      "{t.text}"
                    </blockquote>
                    <div className="flex flex-col items-start md:items-center gap-1 md:gap-2">
                      <div className="text-body md:text-tag-lg font-semibold text-landing-primary">{t.author}</div>
                      {t.title && <div className="text-body md:text-tag-md text-landing-secondary">{t.title}</div>}
                    </div>
                  </motion.div>
                </div>
              </div>
            </div>

            {/* Bottom: Logo Row with individual progress bars */}
            {/* Mobile: 2x2 Grid with 4 testimonials */}
            <div className="block md:hidden">
              <div className="grid grid-cols-2 gap-4 max-w-sm mx-auto">
                {logoRow.slice(0, mobileLogos).map((logo, idx) => (
                  <div key={logo.name + idx} className="flex flex-col items-center gap-1">
                    <button
                      onClick={() => handleLogoClick(idx)}
                      className={`object-contain transition-opacity duration-200 ${
                        idx === activeIndex ? "opacity-100" : "opacity-50"
                      }`}
                      style={{ border: "none", background: "none", padding: 0, cursor: "pointer" }}
                      aria-label={`Show testimonial for ${logo.name}`}
                    >
                      <img src={logo.logo} alt={logo.name} className="w-[80px] h-[32px] object-contain" />
                    </button>
                    {/* Individual progress bar under each logo */}
                    <div
                      className="h-1 rounded transition-all duration-300 w-[80px]"
                      style={{
                        position: "relative",
                        overflow: "hidden",
                        background: idx === activeIndex ? "#e5e7eb" : "#f3f4f6",
                      }}
                    >
                      {idx === activeIndex && (
                        <div
                          className="absolute left-0 top-0 h-full rounded transition-all duration-100 ease-linear"
                          style={{
                            width: `${progress * 100}%`,
                            backgroundColor: "var(--landing-cta-light)",
                          }}
                        />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Desktop: Horizontal Row with 5 testimonials */}
            <div className="hidden md:flex justify-center items-center gap-4 md:gap-6 lg:gap-8 flex-wrap">
              {logoRow.map((logo, idx) => (
                <div key={logo.name + idx} className="flex flex-col items-center gap-1 md:gap-2">
                  <button
                    onClick={() => handleLogoClick(idx)}
                    className={`object-contain transition-opacity duration-200 ${
                      idx === activeIndex ? "opacity-100" : "opacity-50"
                    }`}
                    style={{ border: "none", background: "none", padding: 0, cursor: "pointer" }}
                    aria-label={`Show testimonial for ${logo.name}`}
                  >
                    <img src={logo.logo} alt={logo.name} className="w-[80px] h-[32px] object-contain" />
                  </button>
                  {/* Individual progress bar under each logo */}
                  <div
                    className="h-1 rounded transition-all duration-300 w-[80px]"
                    style={{
                      position: "relative",
                      overflow: "hidden",
                      background: idx === activeIndex ? "#e5e7eb" : "#f3f4f6",
                    }}
                  >
                    {idx === activeIndex && (
                      <div
                        className="absolute left-0 top-0 h-full rounded transition-all duration-100 ease-linear"
                        style={{
                          width: `${progress * 100}%`,
                          backgroundColor: "var(--landing-cta-light)",
                        }}
                      />
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default Testimonial;
